import type { CSSProperties } from 'react';

import type { popstore } from '@ps/types';

import { findExportedFontByFamily } from './fonts';

// ── Constants ──────────────────────────────────────────────

const kDefaultFontSize = 14;

const kSizeMap: Record<string, number> = {
  xs: 0.75,
  sm: 0.9,
  md: 1.0,
  std: 1.25,
  lg: 1.5,
  xl: 2.0,
  xxl: 3.5,
};

/** Legacy's border width for a button that sets a colour but no width. */
export const kDefaultBorderWidth = 2;
export const kDefaultButtonShape: popstore.ButtonShapeType =
  'rounded' as popstore.ButtonShapeType;
export const kDefaultButtonTextAlign: popstore.TextAlignment =
  'center' as popstore.TextAlignment;
export const kDefaultButtonTextVAlign: popstore.VerticalAlignment =
  'middle' as popstore.VerticalAlignment;
export const kDefaultButtonContentPadding = 16;
export const kCustomButtonPlaceholderTitleText = 'Your Button Title';

// ── Color helpers ──────────────────────────────────────────

const noneOrElse = (value: unknown) => (value === 'none' ? undefined : value);

export const popStoreColorNormalized = (
  theme: popstore.editor.ThemeConfig,
  color: popstore.Color | undefined,
): string | [string, string] | undefined => {
  if (color == null) return undefined;
  if (Array.isArray(color)) return color;
  if (typeof color === 'string' && color.startsWith('_')) {
    const value =
      theme.predefinedColors[color as unknown as popstore.PredefinedColor];
    return value === 'transparent' ? undefined : value;
  }
  return color as string;
};

// ── Font size ──────────────────────────────────────────────

export const fontSizeNameToValue = (
  name: popstore.FontSize | string | undefined,
  scale = 1,
): number => {
  if (name === 'xxl-120') return 4 * kDefaultFontSize * scale;
  if (name === 'xxl-140') return 4.5 * kDefaultFontSize * scale;
  if (name === 'xxl-160') return 5 * kDefaultFontSize * scale;
  return (kSizeMap[name as string] ?? 1.25) * kDefaultFontSize * scale;
};

// ── Text style merge ───────────────────────────────────────

const mergedTextStyle = (
  config: popstore.TextStyle | undefined,
  base: popstore.TextStyle | undefined,
  dft: popstore.TextStyle | undefined,
): popstore.TextStyle => {
  return {
    family: config?.family ?? base?.family ?? dft?.family,
    size: config?.size ?? base?.size ?? dft?.size,
    color: (noneOrElse(config?.color) ??
      noneOrElse(base?.color) ??
      noneOrElse(dft?.color)) as popstore.Color | undefined,
    opacity: config?.opacity ?? base?.opacity ?? dft?.opacity,
  };
};

/**
 * 1:1 port of legacy `flows/bio/utils.ts` `textStyleFromConfig`
 * (lines 108-179).
 *
 * Resolution rules (verbatim):
 *   - family lookup via `findExportedFontByFamily`. When found,
 *     emit the registry's `fontFamily` + `fontWeight` + (italic
 *     → fontStyle). When NOT found, emit the family verbatim
 *     and apply Baskervville-italic special-case.
 *   - " Variable" suffix → append non-variable fallback to
 *     fontFamily.
 *   - size → kSizeMap multiplier × kDefaultFontSize × scale.
 *   - color → resolved via popStoreColorNormalized; legacy
 *     special-cases the literal string `'none'` and falls back
 *     to `dft.color` (line 160).
 *   - opacity → percentage / 100; falls back to dft.opacity
 *     when calculated value is 0 (line 166).
 *   - default fontWeight → '400' if not set by registry.
 *
 * Web platform (always true here): adds `transition` to match
 * legacy line 175.
 */
export const textStyleFromConfig = (
  theme: popstore.editor.ThemeConfig,
  config: popstore.TextStyle | undefined,
  base: popstore.TextStyle | undefined,
  dft: popstore.TextStyle | undefined,
  scale = 1,
  lineHeightScale?: number,
): CSSProperties => {
  const merged = mergedTextStyle(config, base, dft);
  const styles: CSSProperties = {};

  if (merged.family) {
    const fontConfig = findExportedFontByFamily(merged.family);
    if (fontConfig) {
      // Emit the BASE family name + matching weight + style.
      // Google Fonts doesn't serve a family literally named
      // "Lexend Semi Bold" — it serves "Lexend" at weight 600.
      // The variant-suffixed name was only ever a UI label
      // legacy used in dropdowns. CSS needs the base family so
      // the browser finds the loaded `@font-face` declaration.
      //
      // Falls back to the variant name when no base is set
      // (e.g. self-hosted custom fonts where the variant name
      // IS the registered face).
      styles.fontFamily = `"${fontConfig.baseFontFamily ?? fontConfig.fontFamily}"`;
      styles.fontWeight = fontConfig.fontWeight;
      if (fontConfig.fontStyle) {
        styles.fontStyle = fontConfig.fontStyle;
      }
    } else {
      // Default behaviour for fonts without a registry mapping.
      styles.fontFamily = `"${merged.family}"`;
      // Legacy Baskervville special-case (line 220-227): when
      // the font name contains "baskervville", apply italic so
      // @fontsource works with the base name + italic style.
      const isBaskervville =
        typeof merged.family === 'string' &&
        merged.family.toLowerCase().includes('baskervville');
      if (isBaskervville) {
        styles.fontStyle = 'italic';
      }
    }

    // Legacy line 231-233: " Variable" suffix → append a non-
    // variable fallback so browsers without variable-font
    // support still load the static face.
    if (merged.family.endsWith(' Variable')) {
      styles.fontFamily += `, "${merged.family.replace(' Variable', '')}"`;
    }
  }

  if (merged.size) {
    styles.fontSize = fontSizeNameToValue(merged.size, scale);
    if (lineHeightScale != null) {
      // Emit as a px string — React inline `lineHeight` treats
      // unitless numbers as a multiplier (CSS spec), so a raw
      // `17.5` becomes `1750%` instead of `17.5px`.
      styles.lineHeight = `${(styles.fontSize as number) * lineHeightScale}px`;
    }
  }

  if (merged.color) {
    const color = popStoreColorNormalized(theme, merged.color);
    if (Array.isArray(color)) {
      // Gradient text colors: not yet supported (matches
      // legacy todo comment line 158).
    } else if (color) {
      // Legacy line 160: when resolved is the string 'none',
      // fall back to dft.color rather than emitting 'none'.
      styles.color = color === 'none' ? (dft?.color as string) : color;
    }
  }

  if (merged.opacity) {
    const opacity = merged.opacity / 100;
    // Legacy line 166: fall back to dft.opacity when the
    // calculated value is 0 (creator might pick 0 by accident
    // — render as the default rather than invisible text).
    styles.opacity = !opacity ? dft?.opacity : opacity;
  }

  // Legacy line 169-173: default to '400' if registry didn't
  // supply a weight. Goes AFTER the family resolution so a
  // registered font's weight isn't clobbered.
  if (!styles.fontWeight) {
    styles.fontWeight = '400';
  }

  styles.transition = 'all 0.2s ease-in-out';

  return styles;
};

// ── Standard button text style cascade ────────────────────
//
// Cascade order (matching RN):
//   1. button's own style (button.title?.style / button.subTitle?.style)
//   2. customization globals (baseButtonTextStyle / baseButtonSubTextStyle + baseTextStyle family fallback)
//   3. hard-coded defaults (size, color from theme)

interface ButtonTextCascadeInput {
  theme: popstore.editor.ThemeConfig;
  customization: popstore.Customization;
  /**
   * Override the default font family for the dft layer. Defaults to
   * `'DM Sans'` — every custom button uses the same fallback chain:
   *   1. button's own `title.style` / `subTitle.style`
   *   2. `customization.baseButtonTextStyle` / `baseButtonSubTextStyle`
   *   3. `'DM Sans'`
   * Callers SHOULD NOT override unless they have a documented legacy
   * reason — passing a different family breaks the "matches the
   * bio's body text" contract.
   */
  defaultFamily?: string;
}

/**
 * Title text style with the canonical 3-layer cascade:
 *   1. `button.title.style` (per-button override) — wins
 *   2. `customization.baseButtonTextStyle` (+ `baseTextStyle.family` /
 *      `theme.styles.customButtonText.color` fallbacks)
 *   3. `'DM Sans'` default at size `std`, color
 *      `theme.styles.customButtonText.color`
 *
 * Mirrors legacy
 * `flows/bio/pages/home/components/custom-buttons/common/content.tsx`
 * `appliedTextStyle`. `defaultFamily` defaults to `'DM Sans'` so any
 * caller that omits it still gets the canonical fallback.
 */
export const buttonTitleStyle = (
  { theme, customization, defaultFamily = 'DM Sans' }: ButtonTextCascadeInput,
  button: popstore.CustomButton,
): CSSProperties =>
  textStyleFromConfig(
    theme,
    button.title?.style,
    {
      ...customization.baseButtonTextStyle,
      opacity: 0,
      family:
        customization.baseButtonTextStyle?.family ??
        customization.baseTextStyle?.family,
      color:
        customization.baseButtonTextStyle?.color ??
        (theme.styles.customButtonText.color as popstore.Color | undefined),
      size: customization.baseButtonTextStyle?.size,
    },
    {
      family: defaultFamily,
      size: 'std' as popstore.FontSize,
      color: (theme.styles.customButtonText.color ??
        customization.baseButtonTextStyle?.color) as popstore.Color | undefined,
    },
  );

/**
 * Subtitle text style with the canonical 3-layer cascade (mirroring
 * the title cascade above). Subtitle inherits the title's color when
 * the subtitle has NO explicit color of its own — legacy
 * `appliedSubTextStyle` reads `title.style.color ?? subTitle.style.color`,
 * i.e. the other way round, which makes a creator-set subtext color a
 * no-op on every button whose title color is set (and the editor's
 * "Button Subtext Color" field silently dead). An explicit subtitle
 * color is the more specific choice, so it wins here; the inheritance
 * it replaced still applies whenever the subtitle has none. Default
 * size is `sm`.
 */
export const buttonSubtitleStyle = (
  { theme, customization, defaultFamily = 'DM Sans' }: ButtonTextCascadeInput,
  button: popstore.CustomButton,
): CSSProperties =>
  textStyleFromConfig(
    theme,
    {
      ...button.subTitle?.style,
      color: (button.subTitle?.style?.color ?? button.title?.style?.color) as
        | popstore.Color
        | undefined,
    },
    {
      ...customization.baseButtonSubTextStyle,
      opacity: 75,
      family:
        customization.baseButtonSubTextStyle?.family ??
        customization.baseTextStyle?.family,
      color: (button.title?.style?.color ??
        customization.baseButtonTextStyle?.color ??
        theme.styles.customButtonText.color) as popstore.Color | undefined,
      size: customization.baseButtonSubTextStyle?.size,
    },
    {
      family: defaultFamily,
      size: 'sm' as popstore.FontSize,
      color: (button.title?.style?.color ??
        theme.styles.customButtonText.color ??
        customization.baseButtonTextStyle?.color) as popstore.Color | undefined,
    },
  );

/**
 * The colour a digital-product card's category tag and price take: the
 * description's own colour when it has one, else the subtext cascade. ONE
 * function because two paths render that card — the live bio's
 * `digitalProductViewModel` and the editor preview's `DigitalProductButton` —
 * and a copy in each would drift (QA).
 */
export const digitalProductSecondaryTextColor = (
  theme: popstore.editor.ThemeConfig,
  customization: popstore.Customization,
  descriptionColor: popstore.Color | undefined,
): popstore.Color | undefined =>
  (descriptionColor ??
    customization.baseButtonSubTextStyle?.color ??
    customization.baseButtonTextStyle?.color ??
    theme.styles.customButtonText.color) as popstore.Color | undefined;

// ── Button style merge ─────────────────────────────────────

const mergedButtonStyle = (
  config: popstore.ButtonStyle | undefined,
  base: popstore.ButtonStyle | undefined,
  dft: popstore.ButtonStyle,
): popstore.ButtonStyle => {
  return {
    bgColor: (noneOrElse(config?.bgColor) ??
      noneOrElse(base?.bgColor) ??
      noneOrElse(dft.bgColor)) as popstore.Color | undefined,
    borderColor: (noneOrElse(config?.borderColor) ??
      noneOrElse(base?.borderColor) ??
      noneOrElse(dft.borderColor)) as popstore.Color | undefined,
    borderWidth: config?.borderWidth ?? base?.borderWidth ?? dft.borderWidth,
    height: config?.height ?? base?.height ?? dft.height,
    shape: config?.shape ?? base?.shape ?? dft.shape,
  };
};

export const buttonStyleFromConfig = (
  theme: popstore.editor.ThemeConfig,
  button: popstore.CustomButton | undefined,
  base: popstore.ButtonStyle | undefined,
  dft: popstore.ButtonStyle,
  options?: {
    includeBackgroundImage?: boolean;
    preview?: boolean;
    /** True when the button's image is aspect-ratio-locked AND its metadata
     *  is known — the "Fit button to background image size" toggle. An
     *  explicit `config.height` (e.g. a creator-wide default button height)
     *  otherwise always wins over CSS `aspect-ratio`, silently defeating the
     *  toggle — so we skip applying it in that case and let aspect-ratio
     *  drive the height instead. */
    aspectRatioOverride?: boolean;
  },
): CSSProperties => {
  const config = mergedButtonStyle(button?.style, base, dft);
  const styles: CSSProperties = {};

  // Background color — Video type doesn't get bgColor unless in preview mode
  if (
    config.bgColor &&
    config.bgColor !== 'none' &&
    (button?.type !== 'video' || options?.preview)
  ) {
    const color = popStoreColorNormalized(theme, config.bgColor);
    if (typeof color === 'string') {
      styles.backgroundColor = color;
    }
  }

  // ── Border ───────────────────────────────────────────────
  //
  // Web-specific divergence from legacy: we ONLY emit border
  // properties when an actual borderColor resolves to a string.
  // Legacy is React Native, where unset borderColor means "no
  // border" regardless of width/style. On web, the global
  // shadcn reset (`* { @apply border-border }` in globals.css)
  // provides a default border-color, so emitting `borderStyle:
  // solid` + `borderWidth` without an explicit color paints a
  // visible theme-coloured border on every button — which
  // legacy never does.
  //
  // Resolve the color FIRST. Only if it's a usable string do we
  // emit width / style / color together. This preserves legacy
  // intent: a border appears only when the creator picks one.

  const resolvedBorderColor =
    config.borderColor && config.borderColor !== 'none'
      ? popStoreColorNormalized(theme, config.borderColor)
      : undefined;
  const borderColorStr =
    typeof resolvedBorderColor === 'string' ? resolvedBorderColor : undefined;

  if (borderColorStr) {
    styles.borderColor = borderColorStr;
    styles.borderStyle = 'solid';
    // A ZERO width with a colour set still paints at the default — 1:1 with
    // legacy (`if (config?.borderWidth) …; if (!styles.borderWidth) styles
    // .borderWidth = 2`). `??` kept a literal `0` from the appearance
    // settings' `baseButtonStyle.borderWidth`, so the creator's border colour
    // resolved and then painted nothing on every button (QA).
    styles.borderWidth = config.borderWidth || kDefaultBorderWidth;
  }

  // Border radius — pill exception list mirrors legacy
  // `flows/bio/utils.ts buttonStyleFromConfig`. These types
  // downgrade `pill` to a 9px radius so they don't end up as
  // ovals at large heights. `calendar` is in the list here but
  // not in legacy's: legacy's shape PICKER already excludes pill
  // for Calendar (`ButtonShapePicker`), so a pill value can only
  // reach a calendar card from older or other-client data — and
  // it must not render as an oval there either.
  const buttonType = button?.type;
  if (config.shape === 'rounded') {
    styles.borderRadius = 9;
  } else if (config.shape === 'pill') {
    if (
      buttonType === 'calendar' ||
      buttonType === 'poll' ||
      buttonType === 'tipping' ||
      buttonType === 'video' ||
      buttonType === 'music' ||
      buttonType === 'digital-product' ||
      buttonType === 'simple-lead-capture'
    ) {
      styles.borderRadius = 9;
    } else {
      styles.borderRadius = 1000;
    }
  } else {
    styles.borderRadius = 0;
  }

  // Height — suppressed when aspect-ratio should drive it instead.
  if (
    config.height != null &&
    config.height !== 'auto' &&
    !options?.aspectRatioOverride
  ) {
    styles.height = config.height;
  }

  // Background image
  if (
    ((buttonType === 'text' && button?.image?.file?.url) ||
      buttonType === 'chat' ||
      buttonType === 'follow' ||
      buttonType === 'tipping' ||
      buttonType === 'community' ||
      buttonType === 'digital-product' ||
      buttonType === 'contact-card') &&
    button?.image?.file &&
    options?.includeBackgroundImage
  ) {
    styles.backgroundImage = `url(${button.image.file.url})`;
    styles.backgroundPosition = 'center center';
    styles.backgroundSize = 'cover';
    styles.backgroundRepeat = 'no-repeat';
  }

  styles.transition = 'opacity 0.2s ease-in-out';

  return styles;
};

// ── Bio font-family resolver ──────────────────────────────

/**
 * Translates a creator's chosen text-style `family` into the
 * actual web `fontFamily` string (with quotes) we feed into
 * inline `style={...}`. Mirrors what `textStyleFromConfig`
 * does internally for `style.fontFamily`, exposed standalone
 * so callers that only need the family name (e.g. the bio's
 * content-tabs strip + the series/collection/post tab body
 * headings) can resolve it without going through the full
 * text-style cascade.
 *
 * Output shape:
 *   - registered Google/local font → `"<BASE family>"` (the actual loaded
 *     `@font-face` name, e.g. "Barlow Condensed" for the variant "Barlow
 *     Condensed Medium") — NOT the variant-suffixed alias.
 *   - unregistered string → `"<raw family>"`
 *   - undefined / unset → `"DM Sans"` (legacy default).
 *
 * CRITICAL: emit the BASE family, mirroring `textStyleFromConfig`
 * (`baseFontFamily ?? fontFamily`). Google Fonts loads an `@font-face` named for
 * the base ("Barlow Condensed"), served at the variant's weight — there is no
 * face literally named "Barlow Condensed Medium". Returning the alias makes the
 * browser miss the loaded face and fall back to a system font (Chrome fuzzy-
 * matches; Safari is strict, so the button/series title rendered in the wrong
 * font — QA). The variant's WEIGHT is not encoded here (this returns a family
 * string only); callers that need the exact weight should use the full
 * `textStyleFromConfig` cascade, or set `fontWeight` alongside.
 */
export function resolveBioFontFamily(family: string | undefined): string {
  if (!family) return '"DM Sans"';
  const registered = findExportedFontByFamily(family);
  if (registered) {
    return `"${registered.baseFontFamily ?? registered.fontFamily}"`;
  }
  return `"${family}"`;
}

// ── Image metadata ────────────────────────────────────────

// Single owner of the `_m` / `_metadata` URL decode — shared with PopImage.
export { imageMetadataFromUrl } from '@ps/functions/utils/image-metadata';

/**
 * The background-image URL to feed a variant's {@link CustomButtonAspectRatioSizer}
 * — the button's `image` URL WHEN the creator locked the aspect ratio
 * ("fit button to image size"), else `undefined` (no sizer; the container keeps
 * its per-type min-height). `customButtonStyle` drops its CSS aspect-ratio +
 * min-height for exactly this case, so the sizer's intrinsic-ratio `<img>` is
 * what drives the card height — 1:1 with legacy's runtime `new Image()` sizing,
 * needing no URL-embedded metadata. Shared by every bg-image variant view.
 */
export function bgAspectSizerUrl(
  button: popstore.CustomButton,
): string | undefined {
  return button.image?.aspectRatioLocked === true
    ? button.image?.file?.url
    : undefined;
}

/** A long episode title otherwise overruns the music card's fixed row. */
const kMusicTitleMaxChars = 64;

export function clampMusicTitle(title: string): string {
  return title.length > kMusicTitleMaxChars
    ? `${title.slice(0, kMusicTitleMaxChars).trimEnd()}…`
    : title;
}

/**
 * Music card row geometry, shared by the visitor card and its editor twin:
 * `CustomButtonMusicPlayer` places its chip and seek bar from these, so a value
 * drifting in one card mis-places the chip there only.
 */
export const kMusicThumbnailWidth = 100;
export const kMusicRowHeight = 110;
